'use client'
import { useEffect, useState } from 'react'

import { Pagination as PaginationUI, PaginationContent, PaginationEllipsis, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination'

const MINIMUM_PAGINATION_ITEMS = 4
const PAGINATE_GROUP = 3

/**
 *
 * @param {number} totalItems - Total number of items
 * @param {number}  limit - Number of items to be displayed on one page
 * @param {number}  currentPage - Currently selected page ( this will come from the query params or it will be 1 if theres no param related to page no)
 * @param {string=} params - This is to make sure we are appending all the right params while changing page numbers (its optional)
 * @returns
 */

export const Pagination = ({
  totalItems,
  limit,
  currentPage,
  params = '',
  onPageChange,
}: {
  totalItems: number
  limit: number
  currentPage: number
  params?: string
  onPageChange?: (page: number) => void
}) => {
  const totalPaginationLinks = Array.from({ length: Math.ceil(totalItems / limit) }, (_, index) => index + 1)
  const firstPageLink = totalPaginationLinks[0] ?? 1
  const lastPageLink = totalPaginationLinks[totalPaginationLinks.length - 1] ?? 1
  const [selectedPage, setSelectedPage] = useState(currentPage)

  const RENDER_CONDITIONS = {
    /** For case when we have only 4 or less pages */
    CASE_1: totalPaginationLinks.length <= MINIMUM_PAGINATION_ITEMS,
    /** For case when we have more than 4 pages and selected page is from first 3 pages */
    CASE_2: totalPaginationLinks.length > MINIMUM_PAGINATION_ITEMS && selectedPage <= PAGINATE_GROUP,
    /** For case when we have more than 4 pages and selected page is from last 3 pages */
    CASE_3: totalPaginationLinks.length > MINIMUM_PAGINATION_ITEMS && selectedPage > lastPageLink - PAGINATE_GROUP && selectedPage > PAGINATE_GROUP,
    /** For case when we have more than 4 pages and selected page is from somewhere in between */
    CASE_4: totalPaginationLinks.length > MINIMUM_PAGINATION_ITEMS && selectedPage <= lastPageLink - PAGINATE_GROUP && selectedPage > PAGINATE_GROUP,
  }

  const ARRAY_SLICES = {
    /** For case when we have more than 4 pages and selected page is from first 3 pages */
    CASE_2: totalPaginationLinks.slice(0, 4),
    /** For case when we have more than 4 pages and selected page is from last 3 pages */
    CASE_3: totalPaginationLinks.slice(totalPaginationLinks.length - 4, totalPaginationLinks.length),
    /** For case when we have more than 4 pages and selected page is from somewhere in between */
    CASE_4: totalPaginationLinks.slice(selectedPage - 2, selectedPage + 1),
  }

  useEffect(() => {
    setSelectedPage(currentPage)
  }, [currentPage])

  const getLinkURL = (pageNumber: number) => {
    return params ? `?page=${pageNumber}&${params}` : `?page=${pageNumber}`
  }

  if (!limit) {
    // eslint-disable-next-line no-console
    console.warn('Pagination error: Limit cant be zero')

    return null
  }

  return (
    <PaginationUI className="flex flex-row items-center justify-center gap-1 sm:gap-2">
      <PaginationContent>
        {currentPage > 1 && <PaginationPrevious href={selectedPage - 1 ? getLinkURL(selectedPage - 1) : '#'} />}
        <div className="flex flex-row items-center justify-center">
          {RENDER_CONDITIONS.CASE_1 && (
            <div className="flex flex-row items-center justify-center gap-1 sm:gap-2">
              {totalPaginationLinks.map((page) => (
                <PaginationLink key={page} isActive={page === selectedPage} href={getLinkURL(page)} onClick={() => onPageChange && onPageChange(selectedPage)}>
                  {page}
                </PaginationLink>
              ))}
            </div>
          )}

          {RENDER_CONDITIONS.CASE_2 && (
            <div className="flex flex-row items-center justify-center gap-1 sm:gap-2">
              <div className="flex flex-row items-center justify-center gap-1 sm:gap-2">
                {ARRAY_SLICES.CASE_2.map((page) => (
                  <PaginationLink key={page} isActive={page === selectedPage} href={getLinkURL(page)} onClick={() => onPageChange && onPageChange(page)}>
                    {page}
                  </PaginationLink>
                ))}
              </div>
              <PaginationEllipsis />
              <PaginationLink
                key={lastPageLink}
                isActive={lastPageLink === selectedPage}
                href={getLinkURL(lastPageLink)}
                onClick={() => onPageChange && onPageChange(selectedPage)}
              >
                {lastPageLink}
              </PaginationLink>
            </div>
          )}

          {RENDER_CONDITIONS.CASE_3 && (
            <div className="flex flex-row items-center justify-center gap-1 sm:gap-2">
              <PaginationLink
                key={firstPageLink}
                isActive={firstPageLink === selectedPage}
                href={getLinkURL(firstPageLink)}
                onClick={() => onPageChange && onPageChange(selectedPage)}
              >
                {firstPageLink}
              </PaginationLink>
              <PaginationEllipsis />
              <div className="flex flex-row items-center justify-center gap-1 sm:gap-2">
                {ARRAY_SLICES.CASE_3.map((page) => (
                  <PaginationLink key={page} isActive={page === selectedPage} href={getLinkURL(page)} onClick={() => onPageChange && onPageChange(page)}>
                    {page}
                  </PaginationLink>
                ))}
              </div>
            </div>
          )}

          {RENDER_CONDITIONS.CASE_4 && (
            <div className="flex flex-row items-center justify-center gap-1 sm:gap-2">
              <PaginationLink
                key={firstPageLink}
                isActive={firstPageLink === selectedPage}
                href={getLinkURL(firstPageLink)}
                onClick={() => onPageChange && onPageChange(selectedPage)}
              >
                {firstPageLink}
              </PaginationLink>
              <PaginationEllipsis />
              <div className="flex flex-row items-center justify-center gap-1 sm:gap-2">
                {ARRAY_SLICES.CASE_4.map((page) => (
                  <PaginationLink key={page} isActive={page === selectedPage} href={getLinkURL(page)} onClick={() => onPageChange && onPageChange(page)}>
                    {page}
                  </PaginationLink>
                ))}
              </div>
              <PaginationEllipsis />
              <PaginationLink
                key={lastPageLink}
                isActive={lastPageLink === selectedPage}
                href={getLinkURL(lastPageLink)}
                onClick={() => onPageChange && onPageChange(selectedPage)}
              >
                {lastPageLink}
              </PaginationLink>
            </div>
          )}
        </div>
        {currentPage < totalPaginationLinks.length && (
          <PaginationNext
            href={selectedPage + 1 <= totalPaginationLinks.length ? getLinkURL(selectedPage + 1) : '#'}
            onClick={() => onPageChange && onPageChange(selectedPage + 1)}
          />
        )}
      </PaginationContent>
    </PaginationUI>
  )
}
